import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { ArrowLeft, Search } from 'lucide-react';
import { api } from '../services/api';
import { fmtTs } from '../utils/time';

/**
 * Everything the app does by itself (owner, Oct 20 2026: "I want a list of all
 * my automations just so I know, and all added ones get added to the list on
 * the app"). Renders GET /api/automations — server/services/automations.js,
 * which a server test keeps complete: a new timer, hook or start-up step
 * without an entry there fails the build's tests.
 *
 * Read only. Every switch stays where it already is; each row says where.
 */
type Reach = 'customers' | 'you' | 'crew' | 'nobody';
type Automation = {
  key: string; group: string; name: string; when: string; does: string;
  reaches: Reach[]; affects: Exclude<Reach, 'nobody'>[]; sends: boolean; money: boolean; texts: string[];
  switch: null | { setting?: string; default?: string; values?: string[]; env?: string | (string | string[])[]; per?: string; outside?: string; where?: string };
  state: string; state_words: string;
};
type Reply = {
  automations: Automation[]; groups: { name: string; count: number }[];
  counted: { total: number; on: number; off: number; outside: number; texts_customers: number };
  practice: boolean; counted_at: string;
};

const PILL: Record<string, string> = {
  on: 'bg-green-50 text-green-700 border-green-200',
  always: 'bg-green-50 text-green-700 border-green-200',
  set: 'bg-green-50 text-green-700 border-green-200',
  rehearsal: 'bg-amber-50 text-amber-800 border-amber-200',
  review: 'bg-amber-50 text-amber-800 border-amber-200',
  per: 'bg-blue-50 text-blue-700 border-blue-200',
  off: 'bg-gray-100 text-gray-600 border-gray-200',
  not_set: 'bg-gray-100 text-gray-600 border-gray-200',
  outside: 'bg-gray-100 text-gray-600 border-gray-200',
};
const REACH_WORDS: Record<Reach, string> = { customers: 'Customers', you: 'You', crew: 'Crew', nobody: 'Nobody' };
const AFFECT_WORDS: Record<string, string> = { customers: "Customers' bills or visits", you: 'Your books', crew: "Crew schedules or pay" };
const RUNNING = new Set(['on', 'always', 'set', 'per', 'rehearsal']);

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'on', label: 'On' },
  { key: 'off', label: 'Off' },
  { key: 'customers', label: 'Texts customers' },
  { key: 'money', label: 'Money' },
] as const;
type FilterKey = typeof FILTERS[number]['key'];

function keep(a: Automation, f: FilterKey) {
  if (f === 'on') return RUNNING.has(a.state);
  if (f === 'off') return ['off', 'not_set', 'review'].includes(a.state);
  if (f === 'customers') return a.sends && a.reaches.includes('customers');
  if (f === 'money') return a.money;
  return true;
}

// A Settings place links straight to its section (or its block): the words are "Settings → <section> → <block> → …".
const SETTINGS_SECTION: Record<string, string> = {
  'Company': 'company', 'Texts & email': 'texts', 'Bids & quotes': 'bids', 'Billing': 'billing', 'Crew & pay': 'crew', 'Money': 'money', 'Automations & logs': 'automations', 'Admin': 'admin',
};
const SETTINGS_BLOCK: Record<string, string> = {
  'Mowing-day texts': 'mowing-day-texts', 'Automatic quotes': 'automatic-quotes', 'Winter work': 'winter-work', 'Instant quote page': 'instant-quote',
  'Quote visits customers can book': 'quote-visits', 'Bank accounts': 'bank-accounts', 'Late fees': 'late-fees',
};
function settingsLink(where: string): string {
  const parts = where.split(' → ').map(p => p.replace(/\s*\(.*$/, '').trim());
  const hash = SETTINGS_BLOCK[parts[2] || ''] || SETTINGS_SECTION[parts[1] || ''] || '';
  return hash ? `/settings#${hash}` : '/settings';
}

/** Where to switch it, in words — a Settings place links to Settings, at the section it names. */
function Where({ a }: { a: Automation }) {
  const sw = a.switch;
  if (!sw) return <p className="text-xs text-gray-500">No switch — it always runs when that happens.</p>;
  const where = sw.where || sw.outside || '';
  const label = sw.outside ? 'Turn it off' : sw.env ? 'Set up' : sw.per ? 'Set' : 'Switch';
  return (
    <p className="text-xs text-gray-600">
      <span className="font-semibold text-gray-700">{label}: </span>
      {where.startsWith('Settings') ? <Link to={settingsLink(where)} className="text-brand-700 underline">{where}</Link> : where}
    </p>
  );
}

function Row({ a }: { a: Automation }) {
  return (
    <div className="px-4 py-3 space-y-1.5">
      {/* On a phone the state sits under the name, so a long name is never squeezed; side by side from sm up. */}
      <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
        <p className="text-sm font-bold text-gray-900 min-w-0">{a.name}</p>
        <span className={`self-start flex-shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${PILL[a.state] || PILL.off}`}>{a.state_words}</span>
      </div>
      <p className="text-sm text-gray-700">{a.does}</p>
      <p className="text-xs text-gray-500"><span className="font-semibold text-gray-600">When: </span>{a.when}</p>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[11px] text-gray-500">{a.sends ? 'Texts or emails:' : 'Tells:'}</span>
        {a.reaches.map(r => (
          <span key={r} className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">{REACH_WORDS[r] || r}</span>
        ))}
        {a.money && <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">Money</span>}
      </div>
      {(a.affects || []).length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] text-gray-500">Changes:</span>
          {a.affects.map(r => <span key={r} className="text-[11px] px-2 py-0.5 rounded-full border border-gray-200 text-gray-600">{AFFECT_WORDS[r] || r}</span>)}
        </div>
      )}
      <Where a={a} />
    </div>
  );
}

export default function Automations() {
  const { data, isLoading, isError } = useQuery<Reply>('automations', () => api.get('/automations').then(r => r.data), { staleTime: 30_000 });
  const [filter, setFilter] = useState<FilterKey>('all');
  const [q, setQ] = useState('');

  const shown = useMemo(() => {
    const words = q.toLowerCase().split(/\s+/).filter(Boolean);
    return (data?.automations || []).filter(a => keep(a, filter)
      && words.every(w => `${a.name} ${a.does} ${a.when} ${a.group}`.toLowerCase().includes(w)));
  }, [data, filter, q]);

  const c = data?.counted;
  return (
    <div className="space-y-4 max-w-3xl">
      <div>
        <Link to="/settings" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"><ArrowLeft className="h-3.5 w-3.5" /> Settings</Link>
        <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-gray-900">Automations</h1>
        <p className="mt-1 text-sm text-gray-500">Everything the app does by itself. Nothing on this page changes anything — each row says where its switch is.</p>
      </div>

      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
      {isError && <p className="text-sm text-red-700">Could not load the list. Refresh the page to try again.</p>}

      {data && c && (
        <>
          <div className="card px-4 py-3">
            <p className="text-sm font-semibold text-gray-900">
              {c.total} automations · {c.on} on · {c.off} off{c.outside ? ` · ${c.outside} outside the app` : ''}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              {c.texts_customers} can text or email customers right now · as of {fmtTs(data.counted_at, { hour: 'numeric', minute: '2-digit' })}
            </p>
            {data.practice && <p className="text-xs text-amber-800 mt-1">Practice server: texts are only logged, never sent to a phone.</p>}
          </div>

          <div className="relative">
            <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input type="search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search: bill, text, route, bid…" className="input pl-9 w-full" />
          </div>
          <div className="flex gap-2 flex-wrap">
            {FILTERS.map(f => (
              <button key={f.key} type="button" onClick={() => setFilter(f.key)}
                className={`press px-3 py-1.5 text-sm rounded-full font-medium ${filter === f.key ? 'bg-brand-600 text-black' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {f.label}
              </button>
            ))}
          </div>

          {data.groups.map(g => {
            const rows = shown.filter(a => a.group === g.name);
            if (!rows.length) return null;
            return (
              <section key={g.name} className="card p-0 overflow-hidden">
                <h2 className="px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-gray-600 bg-gray-50 border-b border-gray-100">
                  {g.name} <span className="font-normal text-gray-400">· {rows.length}</span>
                </h2>
                <div className="divide-y divide-gray-100">
                  {rows.map(a => <Row key={a.key} a={a} />)}
                </div>
              </section>
            );
          })}
          {!shown.length && <p className="card p-4 text-sm text-gray-500">Nothing matches.</p>}
        </>
      )}
    </div>
  );
}
